import { useState, useEffect } from 'react';
import { Package, MessageSquare, FileText, Users, DollarSign, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [stats, setStats] = useState({ products: 0, quotes: 0, articles: 0, subscribers: 0 });
  const [prices, setPrices] = useState({ gold_nzd: 0, silver_nzd: 0, platinum_nzd: 0, palladium_nzd: 0, copper_nzd: 0 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prods, quotes, arts, subs, p] = await Promise.all([
          axios.get(`${API}/admin/products`),
          axios.get(`${API}/admin/quotes`),
          axios.get(`${API}/admin/articles`),
          axios.get(`${API}/admin/newsletter`),
          axios.get(`${API}/prices`),
        ]);
        setStats({ products: prods.data.length, quotes: quotes.data.length, articles: arts.data.length, subscribers: subs.data.length });
        setPrices({ gold_nzd: p.data.gold_nzd, silver_nzd: p.data.silver_nzd, platinum_nzd: p.data.platinum_nzd, palladium_nzd: p.data.palladium_nzd, copper_nzd: p.data.copper_nzd || 0 });
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
        toast.error('Failed to load dashboard data');
      }
    };
    fetchData();
  }, []);

  const savePrices = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/prices`, prices);
      toast.success('Prices updated');
    } catch (err) {
      console.error('Failed to update prices:', err);
      toast.error('Failed to update prices');
    } finally {
      setSaving(false);
    }
  };

  const statCards = [
    { label: 'Products', value: stats.products, icon: Package, color: 'text-blue-600' },
    { label: 'Quote Requests', value: stats.quotes, icon: MessageSquare, color: 'text-green-600' },
    { label: 'Articles', value: stats.articles, icon: FileText, color: 'text-purple-600' },
    { label: 'Newsletter Subs', value: stats.subscribers, icon: Users, color: 'text-orange-600' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold mb-8" style={{ fontFamily: "'Poppins', sans-serif" }}>Dashboard</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map(s => (
          <Card key={s.label}>
            <CardContent className="p-6 flex items-center gap-4">
              <div className={`p-3 rounded-sm bg-gray-50 ${s.color}`}><s.icon size={24} /></div>
              <div>
                <p className="text-2xl font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>{s.value}</p>
                <p className="text-sm text-gray-500">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2" style={{ fontFamily: "'Poppins', sans-serif" }}><DollarSign size={20} /> Spot Prices (NZD per oz)</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            {[{ key: 'gold_nzd', label: 'Gold' }, { key: 'silver_nzd', label: 'Silver' }, { key: 'platinum_nzd', label: 'Platinum' }, { key: 'palladium_nzd', label: 'Palladium' }, { key: 'copper_nzd', label: 'Copper' }].map(m => (
              <div key={m.key}>
                <Label>{m.label}</Label>
                <Input type="number" step="0.01" value={prices[m.key]} onChange={e => setPrices({ ...prices, [m.key]: parseFloat(e.target.value) || 0 })} data-testid={`price-${m.key}`} />
              </div>
            ))}
          </div>
          <Button onClick={savePrices} disabled={saving} className="btn-gold" data-testid="save-prices-btn">
            {saving ? 'Saving...' : 'Update Prices'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
